import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Stack,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { useSelector, useDispatch } from "react-redux";
import { showErrorSnackbar } from "store/actions/snackbarMessage";
import { updateRestaurantValues } from "store/actions/restaurant";
import ToggleRow from "../ToggleRow";

const TITLE_LIMIT = 50;
const CONTENT_LIMIT = 800;

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(3),
  },
  actions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "flex-end",
  },
  buttonWrapper: {
    position: "relative",
    display: "inline-flex",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

function FirstPageDialogSettings() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const showError = (msg) => dispatch(showErrorSnackbar(msg));
  /** @returns {Promise<unknown>} */
  const updateRestaurant = (values) =>
    dispatch(updateRestaurantValues(values));

  const restaurant = useSelector(
    (state) => state.restaurant.selectedRestaurant
  );

  const [showDialog, setShowDialog] = useState(false);
  const [dialogText, setDialogText] = useState("");
  const [titleText, setTitleText] = useState("");
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    if (!restaurant || !restaurant.onlineSettings) return;
    const onlineSettings = restaurant.onlineSettings;

    if (onlineSettings.showDialog !== undefined && onlineSettings.showDialog !== null) {
      setShowDialog(onlineSettings.showDialog);
    }
    if (onlineSettings.dialogText !== undefined && onlineSettings.dialogText !== null) {
      setDialogText(onlineSettings.dialogText);
    }
    if (onlineSettings.dialogTitle !== undefined && onlineSettings.dialogTitle !== null) {
      setTitleText(onlineSettings.dialogTitle);
    }
  }, [restaurant]);

  const titleError = titleText.length > TITLE_LIMIT;
  const contentError = dialogText.length > CONTENT_LIMIT;

  const handleUpdateText = async (event) => {
    event.preventDefault();
    if (buttonLoading) return;

    if (titleText.length === 0 || titleText.length > TITLE_LIMIT) {
      showError(`Title must be between 1 and ${TITLE_LIMIT} characters`);
      return;
    }

    if (dialogText.length === 0 || dialogText.length > CONTENT_LIMIT) {
      showError(`Dialog text must be between 1 and ${CONTENT_LIMIT} characters`);
      return;
    }

    const htmlTagRegex = /<[^>]*>/;
    if (htmlTagRegex.test(dialogText)) {
      showError("Dialog text cannot contain HTML tags");
      return;
    }

    setButtonLoading(true);
    let onlineSettings = {
      showDialog,
      dialogText,
      dialogTitle: titleText,
    };

    if (restaurant && restaurant.onlineSettings) {
      onlineSettings = {
        ...restaurant.onlineSettings,
        ...onlineSettings,
      };
    }

    try {
      await updateRestaurant({ onlineSettings });
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Welcome Dialog"
        subheader="An optional modal shown to guests the first time they open the ordering site."
      />
      <Divider />
      <CardContent className={classes.content}>
        <Stack spacing={3}>
          <ToggleRow
            checked={showDialog}
            name="showDialog"
            onChange={() => setShowDialog(!showDialog)}
            label="Show the welcome dialog"
            description="When enabled, guests see this dialog before browsing the menu."
          />

          <TextField
            label="Dialog title"
            variant="outlined"
            value={titleText}
            onChange={(e) => setTitleText(e.target.value)}
            fullWidth
            inputProps={{ maxLength: TITLE_LIMIT }}
            error={titleError}
            helperText={`${titleText.length}/${TITLE_LIMIT}`}
            placeholder="e.g. Welcome to our restaurant"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Dialog content"
            variant="outlined"
            multiline
            rows={5}
            value={dialogText}
            onChange={(e) => setDialogText(e.target.value)}
            fullWidth
            inputProps={{ maxLength: CONTENT_LIMIT }}
            error={contentError}
            helperText={`Plain text only, no HTML • ${dialogText.length}/${CONTENT_LIMIT}`}
            placeholder="Share opening hours, promotions, or anything you want guests to see first."
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </CardContent>

      <Box className={classes.actions}>
        <Box className={classes.buttonWrapper}>
          <Button
            onClick={handleUpdateText}
            variant="contained"
            color="primary"
            disabled={buttonLoading}
            size="large"
          >
            Save changes
          </Button>
          {buttonLoading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default FirstPageDialogSettings;
